import type {
  NotificationTargetDto,
  ProviderBooking,
  ProviderFoodOrder,
  ProviderReadOptions,
  ProviderSessionFilters,
  ProviderVenuesResponse,
  UserRole,
} from "../../lib/api";

export type ProviderNotificationTarget =
  | { kind: "booking"; bookingId: string; venueId: string | null }
  | { kind: "session"; sessionId: string; venueId: string | null }
  | { kind: "food-order"; sessionId: string; orderId: string; venueId: string | null }
  | { kind: "venue"; venueId: string }
  | { kind: "pending-confirmations" };

export type ProviderNotificationResolvableTarget =
  | Exclude<ProviderNotificationTarget, { kind: "food-order" }>
  | { kind: "food-order"; orderId: string; venueId: string }
  | { kind: "venue"; venueId: string }
  | { kind: "inbox" };

export type ProviderNotificationAuthorityTarget =
  | { kind: "booking"; bookingId: string; venueId: string | null }
  | { kind: "session"; sessionId: string; venueId: string | null }
  | { kind: "food-order"; orderId: string; sessionId?: string; venueId: string | null }
  | { kind: "venue"; venueId: string }
  | { kind: "pending-confirmations" }
  | { kind: "inbox" };

export type ProviderNotificationResolvedTarget =
  | { kind: "booking"; bookingId: string; venueId: string }
  | { kind: "session"; sessionId: string; venueId: string }
  | { kind: "food-order"; orderId: string; sessionId: string; venueId: string }
  | { kind: "venue"; venueId: string }
  | { kind: "pending-confirmations" }
  | { kind: "inbox" };

export interface ProviderNotificationAuthorityApi {
  getBooking: (bookingId: string, options?: ProviderReadOptions) => Promise<ProviderBooking>;
  listSessions: (filters?: ProviderSessionFilters, options?: ProviderReadOptions) => Promise<{ sessions: Array<{ id: string; venue_id: string }> }>;
  listFoodOrders: (filters?: { sessionId?: string; venueId?: string }, options?: ProviderReadOptions) => Promise<{ orders: ProviderFoodOrder[] }>;
  listVenues: (options?: ProviderReadOptions) => Promise<ProviderVenuesResponse>;
}

export type ProviderNotificationTargetAvailability =
  | { availability: "available"; target: ProviderNotificationResolvableTarget }
  | { availability: "unavailable"; reason: "stale" | "unauthorized" | "invalid" };

/** Converts an authority-checked food order into the existing non-null session deep link. */
export function resolveProviderFoodOrderRouteTarget(
  order: Pick<ProviderFoodOrder, "id" | "session_id" | "venue_id">,
  target: { kind: "food-order"; orderId: string; venueId: string },
): Extract<ProviderNotificationResolvedTarget, { kind: "food-order" }> | null {
  if (
    order.id !== target.orderId ||
    order.venue_id !== target.venueId ||
    typeof order.session_id !== "string" ||
    order.session_id.length === 0
  ) {
    return null;
  }
  return { kind: "food-order", orderId: order.id, sessionId: order.session_id, venueId: order.venue_id };
}

/** Resolves every navigable notification target from current server authority. */
export async function resolveProviderNotificationAuthorityTarget(
  target: ProviderNotificationAuthorityTarget,
  api: ProviderNotificationAuthorityApi,
  isCurrent: () => boolean = () => true,
): Promise<ProviderNotificationResolvedTarget | null | undefined> {
  switch (target.kind) {
    case "booking": {
      const booking = await api.getBooking(target.bookingId, { networkOnly: true });
      if (!isCurrent()) return undefined;
      if (booking.id !== target.bookingId || (target.venueId !== null && booking.venue_id !== target.venueId)) return null;
      return { kind: "booking", bookingId: booking.id, venueId: booking.venue_id };
    }
    case "session": {
      const sessions = await api.listSessions({ venueId: target.venueId ?? undefined }, { networkOnly: true });
      if (!isCurrent()) return undefined;
      const session = sessions.sessions.find((candidate) =>
        candidate.id === target.sessionId && (target.venueId === null || candidate.venue_id === target.venueId),
      );
      return session ? { kind: "session", sessionId: session.id, venueId: session.venue_id } : null;
    }
    case "food-order": {
      const orders = await api.listFoodOrders({ venueId: target.venueId ?? undefined }, { networkOnly: true });
      if (!isCurrent()) return undefined;
      const order = orders.orders.find((candidate) =>
        candidate.id === target.orderId && (target.venueId === null || candidate.venue_id === target.venueId),
      );
      if (!order || (target.sessionId !== undefined && order.session_id !== target.sessionId)) return null;
      const routeTarget = resolveProviderFoodOrderRouteTarget(order, {
        kind: "food-order",
        orderId: target.orderId,
        venueId: order.venue_id,
      });
      return routeTarget ? { ...routeTarget, venueId: order.venue_id } : null;
    }
    case "venue": {
      const venues = await api.listVenues({ networkOnly: true });
      if (!isCurrent()) return undefined;
      return venues.venues.some((venue) => venue.id === target.venueId) ? target : null;
    }
    case "pending-confirmations":
    case "inbox":
      return isCurrent() ? target : undefined;
  }
}

/** Applies an authority result only while the initiating identity generation remains current. */
export async function resolveAndRouteProviderNotificationTarget(
  target: ProviderNotificationAuthorityTarget,
  api: ProviderNotificationAuthorityApi,
  isCurrent: () => boolean,
  actions: {
    onResolved: (target: ProviderNotificationResolvedTarget) => void;
    onUnavailable: (error?: unknown) => void;
  },
): Promise<void> {
  try {
    const resolved = await resolveProviderNotificationAuthorityTarget(target, api, isCurrent);
    if (!isCurrent() || resolved === undefined) return;
    if (resolved === null) {
      if (isCurrent()) actions.onUnavailable();
      return;
    }
    if (isCurrent()) actions.onResolved(resolved);
  } catch (error) {
    if (isCurrent()) actions.onUnavailable(error);
  }
}

/** Converts the frozen V1 server target by string kind, never enum position. */
export function resolveProviderNotificationTarget(
  target: NotificationTargetDto,
  options: { providerId: string | null; role: UserRole | null; expiresAt: string; nowMs?: number },
): ProviderNotificationTargetAvailability {
  if (target.audience !== "provider" || (options.role !== "provider_owner" && options.role !== "provider_staff")) {
    return { availability: "unavailable", reason: "unauthorized" };
  }
  const expiresAt = Date.parse(options.expiresAt);
  if (!Number.isFinite(expiresAt)) return { availability: "unavailable", reason: "invalid" };
  if (expiresAt <= (options.nowMs ?? Date.now())) return { availability: "unavailable", reason: "stale" };
  if (!target.providerId || (options.providerId !== null && target.providerId !== options.providerId)) {
    return { availability: "unavailable", reason: "unauthorized" };
  }
  switch (target.kind) {
    case "booking":
      return target.bookingId && target.venueId
        ? { availability: "available", target: { kind: "booking", bookingId: target.bookingId, venueId: target.venueId } }
        : { availability: "unavailable", reason: "invalid" };
    case "session":
      return target.sessionId && target.venueId
        ? { availability: "available", target: { kind: "session", sessionId: target.sessionId, venueId: target.venueId } }
        : { availability: "unavailable", reason: "invalid" };
    case "food-order":
      return target.foodOrderId && target.venueId
        ? { availability: "available", target: { kind: "food-order", orderId: target.foodOrderId, venueId: target.venueId } }
        : { availability: "unavailable", reason: "invalid" };
    case "venue":
      return target.venueId
        ? { availability: "available", target: { kind: "venue", venueId: target.venueId } }
        : { availability: "unavailable", reason: "invalid" };
    case "pending-confirmations":
      return target.venueId
        ? { availability: "available", target: { kind: "pending-confirmations" } }
        : { availability: "unavailable", reason: "invalid" };
    case "inbox":
      return { availability: "available", target: { kind: "inbox" } };
  }
}

export function parseProviderNotificationTarget(search: string): ProviderNotificationTarget | null {
  const params = new URLSearchParams(search);
  const venueId = params.get("venue_id");
  const bookingId = params.get("booking_id");
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  if (orderId) {
    const uuid = (value: string | null): value is string =>
      value !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
    if (
      !uuid(orderId) ||
      !uuid(venueId) ||
      (sessionId !== null && !uuid(sessionId))
    ) {
      return null;
    }
    if (sessionId) return { kind: "food-order", sessionId, orderId, venueId };
    // URL state cannot claim a session. It is resolved from the order before
    // ProviderLayout routes this target to the Floor context.
    return { kind: "food-order", orderId, venueId } as ProviderNotificationTarget;
  }
  if (bookingId) return { kind: "booking", bookingId, venueId };
  if (sessionId) return { kind: "session", sessionId, venueId };
  if (params.get("notification") === "pending-confirmations") return { kind: "pending-confirmations" };
  if (venueId) return { kind: "venue", venueId };
  return null;
}
